import { useEffect, useState } from 'preact/hooks';
import { listSessions, renameSession, deleteSession } from '../lib/api';
import { activeToken } from '../store/global';
import type { SessionSummary } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { UserFilter } from './Memories';
import { formatRelative, truncate } from '../lib/format';

export function Sessions() {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [userFilter, setUserFilter] = useState('');

  const refresh = async () => {
    if (!activeToken.value) return;
    setLoading(true);
    try {
      // Sem filtro implícito — lista TUDO. Filtro de user_id explícito acima.
      const params: { user_id?: string } = {};
      if (userFilter.trim()) params.user_id = userFilter.trim();
      const res = await listSessions(params);
      const data = Array.isArray(res) ? res : (res as { data: SessionSummary[] }).data || [];
      setSessions(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [activeToken.value, userFilter]);

  const rename = async (id: string) => {
    if (!draft.trim()) return;
    try {
      await renameSession(id, draft.trim());
      setEditing(null);
      refresh();
    } catch (e) {
      alert(`Falha ao renomear: ${(e as Error).message}`);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Excluir esta sessão? Esta ação não pode ser desfeita.')) return;
    try {
      await deleteSession(id);
      refresh();
    } catch (e) {
      alert(`Não foi possível excluir a sessão: ${(e as Error).message}`);
    }
  };

  return (
    <div class="p-6 max-w-6xl">
      <PageHeader
        title="Sessões"
        description="Conversas anteriores agrupadas por session_id. Você pode renomear ou excluir."
        icon={<i class="ph ph-clock-counter-clockwise text-xl" />}
        actions={
          <button
            class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50 disabled:pointer-events-none"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        }
      />

      <UserFilter value={userFilter} onChange={setUserFilter} />

      <DataTable
        rows={sessions as unknown as Array<Record<string, unknown>>}
        rowKey={(r) => (r as unknown as SessionSummary).id}
        columns={[
          {
            key: 'name',
            label: 'Sessão',
            render: (r) => {
              const s = r as unknown as SessionSummary;
              const id = s.id;
              if (editing === id) {
                return (
                  <div class="flex items-center gap-1">
                    <input
                      class="h-8 w-64 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:opacity-50"
                      value={draft}
                      onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
                      autoFocus
                    />
                    <button
                      class="inline-flex items-center justify-center size-8 rounded-md text-success hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      onClick={() => rename(id)}
                      title="Salvar"
                      aria-label="Salvar"
                    >
                      <i class="ph ph-check text-sm" />
                    </button>
                    <button
                      class="inline-flex items-center justify-center size-8 rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      onClick={() => setEditing(null)}
                      title="Cancelar"
                      aria-label="Cancelar"
                    >
                      <i class="ph ph-x text-sm" />
                    </button>
                  </div>
                );
              }
              return (
                <div>
                  <div class="font-medium">{s.session_name || truncate(s.id, 40)}</div>
                  <div class="text-[11px] text-muted-foreground font-mono">{s.id}</div>
                </div>
              );
            },
          },
          {
            key: 'user',
            label: 'User ID',
            width: '18ch',
            render: (r) => {
              const uid = (r as unknown as SessionSummary).user_id;
              if (!uid) return <span class="text-xs text-muted-foreground">—</span>;
              return (
                <button
                  class="font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => setUserFilter(uid)}
                  title="Filtrar por este user_id"
                >
                  {uid}
                </button>
              );
            },
          },
          { key: 'updated', label: 'Atualizado', render: (r) => <span class="text-xs">{formatRelative((r as unknown as SessionSummary).updated_at)}</span>, width: '16ch' },
          {
            key: 'actions',
            label: '',
            width: '8ch',
            render: (r) => {
              const s = r as unknown as SessionSummary;
              return (
                <div class="flex items-center gap-1">
                  <button
                    class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    onClick={() => {
                      setEditing(s.id);
                      setDraft(s.session_name || '');
                    }}
                    title="Renomear"
                    aria-label="Renomear"
                  >
                    <i class="ph ph-pencil-simple text-sm" />
                  </button>
                  <button
                    class="inline-flex items-center justify-center size-8 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-destructive/40"
                    onClick={() => remove(s.id)}
                    title="Excluir"
                    aria-label="Excluir"
                  >
                    <i class="ph ph-trash text-sm" />
                  </button>
                </div>
              );
            },
          },
        ]}
        empty={
          activeToken.value
            ? userFilter
              ? `Nenhuma sessão para o user_id "${userFilter}". Inicie uma conversa no chat para começar.`
              : 'Nenhuma sessão ainda. Inicie uma conversa no chat para começar.'
            : 'Configure o token nas configurações para ver as sessões.'
        }
      />
    </div>
  );
}
